import React from "react";
import * as yup from "yup";
import { useForm } from "react-weaver";
import { useNavigate } from "@reach/router";

import { FormControl, Input } from "kanopi-components/Input";
import { HeadingLarge, ParagraphMedium } from "kanopi-components/typography";
import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { FixedFooter } from "kanopi-components/Sticky/FixedFooter";
import { Form } from "kanopi-components/Form";
import { Kontainer } from "kanopi-components/Layout/Kontainer";
import { Sektion } from "kanopi-components/Layout/Sektion";
import { SidePadder } from "kanopi-components/Layout/SidePadder";
import { toaster } from "kanopi-components/ToastContainer";
import { parseQueryString } from "kanopi-components/utils/browser";

import { usePasswordResetConfirm } from "../hooks/usePasswordResetConfirm";
import { passwordValidator } from "kw/components/RegisterForm/RegisterForm";

export function PasswordResetConfirmPage({
  initialValues = {},
}: {
  initialValues?: {
    token?: string | string[];
    newPassword1?: string;
    newPassword2?: string;
  };
}) {
  const navigate = useNavigate();
  const submitPasswordResetConfirm = usePasswordResetConfirm();

  const getQueryStringToken = () => {
    const token = parseQueryString({ search: location.search })["token"];
    return token?.length > 0 && Array.isArray(token) ? token[0] : token?.toString();
  };

  const queryStringToken = getQueryStringToken();
  initialValues["token"] = queryStringToken || initialValues.token;
  const { fieldProps, submit, loading, errors, notReady } = useForm({
    initialValues,
    validator: yup.object().shape({
      ...passwordValidator,
    }),
    onSubmit: submitPasswordResetConfirm,
  });

  const handleSubmit = async () => {
    try {
      await submit();
      navigate("/password-reset/confirm/success");
    } catch (e) {
      toaster({
        kind: "ERROR",
        content: e.message,
      });
    }
  };

  return (
    <Form>
      <SidePadder>
        <HeadingLarge marginBottom={0} center>
          Choose a new password
        </HeadingLarge>
        <Kontainer>
          <Sektion>
            <Block maxWidth="28rem" margin="auto">
              <ParagraphMedium center marginBottom="scale1000" marginTop={0}>
                Please enter a new password below. It needs to be at least 9 characters
                long and contain both letters and numbers.
              </ParagraphMedium>
              <Input
                type="hidden"
                {...fieldProps.token}
                overrides={{
                  Root: {
                    style: () => ({
                      display: "none",
                    }),
                  },
                }}
              />
              <Block>
                <FormControl label="Choose a password" error={errors?.password1}>
                  <Input type="password" {...fieldProps.password1} />
                </FormControl>
              </Block>
              <Block>
                <FormControl label="Confirm your password" error={errors?.password2}>
                  <Input type="password" {...fieldProps.password2} />
                </FormControl>
              </Block>
            </Block>
          </Sektion>
        </Kontainer>
      </SidePadder>

      <FixedFooter>
        <Sektion>
          <Block display="flex" justifyContent="center">
            <Button
              type="submit"
              kind={Button.KIND.primary}
              wide
              onClick={handleSubmit}
              isLoading={loading}
              disabled={notReady}
            >
              Reset password
            </Button>
          </Block>
        </Sektion>
      </FixedFooter>
    </Form>
  );
}
